import { SxProps, Theme } from '@mui/material';
import {
 DataGridPremiumProps,
 GridFeatureMode,
 GridPremiumSlotsComponent,
 GRID_CHECKBOX_SELECTION_COL_DEF,
 GridColDef,
 GridGroupingColDefOverride,
 GridRenderCellParams,
} from '@mui/x-data-grid-premium';
import { MutableRefObject, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ColPresetType } from '../../../../redux/clientConfiguration/clientConfigurationSlice';
import { AUTOSIZE_DELAY_MS, CHECKBOX_COL_WIDTH } from '../constants';
import CustomColumnMenuWrapper from '../CustomColumnMenu';
import DataGridPagination from '../GridPagination';
import gridSlotsIcons from '../gridSlotsIcons';
import GridToolbar from '../GridToolbar';
import { renderColObj } from '../renderCol';
import RowMenu from '../RowMenu';
import dataGridStyles from '../styles';
import { CustomGridApi } from '../types';
import { useXTableContext } from '../XTableContext';
import { constructRows } from '../utils';

const CHECKBOX_COL: GridColDef = {
 ...GRID_CHECKBOX_SELECTION_COL_DEF,
 width: CHECKBOX_COL_WIDTH,
 maxWidth: CHECKBOX_COL_WIDTH,
};

interface UseGridConfigParams {
 apiRef: MutableRefObject<CustomGridApi>;
 columnsState: GridColDef[];
 currentColumnsPreset: ColPresetType | null;
 rows: DataGridPremiumProps['rows'];
 isServerMode: boolean;
 onRowClick?: DataGridPremiumProps['onRowClick'];
 onCellClick?: DataGridPremiumProps['onCellClick'];
 propSlots?: DataGridPremiumProps['slots'];
}

interface UseGridConfigReturn {
 groupingColDef: GridGroupingColDefOverride;
 processedRows: Record<string, unknown>[];
 allColumns: GridColDef[];
 gridSx: SxProps<Theme>;
 slots: Partial<GridPremiumSlotsComponent>;
 gridMode: GridFeatureMode;
 handleRowGroupingModelChange: () => void;
 getRowClassName: (params: {
  indexRelativeToCurrentPage: number;
  row: Record<string, unknown>;
 }) => string;
}

const useGridConfig = ({
 apiRef,
 columnsState,
 currentColumnsPreset,
 rows,
 isServerMode,
 onRowClick,
 onCellClick,
 propSlots,
}: UseGridConfigParams): UseGridConfigReturn => {
 const { currentId, setCurrentId, setModalState } = useXTableContext();
 const { t } = useTranslation();

 const actionsCol = useMemo(
  () =>
   renderColObj(
    { field: 'actions' },
    '',
    undefined,
    (params: GridRenderCellParams<{ row: Record<string, unknown> }>) => (
     <RowMenu
      row={params.row}
      setCurrentId={setCurrentId}
      setModalState={setModalState}
     />
    ),
   ),
  [setCurrentId, setModalState],
 );

 const groupingColDef: GridGroupingColDefOverride = useMemo(
  () => ({ headerName: t('global.group') }),
  [t],
 );

 const processedRows = useMemo(
  () => constructRows(rows as ReadonlyArray<Record<string, unknown>>),
  [rows],
 );

 const allColumns = useMemo(
  () => [CHECKBOX_COL, ...columnsState, actionsCol],
  [columnsState, actionsCol],
 );

 const gridMode: GridFeatureMode = isServerMode ? 'server' : 'client';

 const handleRowGroupingModelChange = useCallback(() => {
  setTimeout(() => {
   const groupColumns = apiRef.current
    .getAllColumns()
    .filter((col) => col.field.includes('row_group'))
    .map((col) => col.field);

   apiRef.current.autosizeColumns({
    columns: groupColumns,
    includeHeaders: true,
    includeOutliers: true,
   });
  }, AUTOSIZE_DELAY_MS);
 }, [apiRef]);

 const getRowClassName = useCallback(
  (params: {
   indexRelativeToCurrentPage: number;
   row: Record<string, unknown>;
  }) => {
   const parity = params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd';
   const isSelected =
    params.row.Id === currentId &&
    params.row.Id !== undefined &&
    currentId !== undefined;
   const sidebarClass = isSelected ? ' row-sidebar' : '';
   return `${parity}${sidebarClass}`;
  },
  [currentId],
 );

 const gridSx = useMemo(
  () => (theme: Theme) => ({
   ...dataGridStyles(theme),
   '& .MuiDataGrid-row': {
    ...(dataGridStyles(theme)['& .MuiDataGrid-row'] || {}),
    // cursor: onRowClick || onCellClick ? 'pointer' : 'initial',
   },
   ...(currentColumnsPreset?.isPro
    ? {
       '& .limit-span': { fontSize: 12, padding: '2px 4px' },
       '& .MuiDataGrid-columnHeaderTitle': {
        ...(dataGridStyles(theme)['& .MuiDataGrid-columnHeaderTitle'] || {}),
        fontSize: 12,
       },
       '& .MuiDataGrid-cell': {
        ...(dataGridStyles(theme)['& .MuiDataGrid-cell'] || {}),
        fontSize: 12,
        px: '12px',
        display: 'flex',
        alignItems: 'center',
       },
      }
    : {}),
  }),
  [currentColumnsPreset?.isPro, onRowClick, onCellClick],
 );

 const slots = useMemo(
  () => ({
   ...propSlots,
   columnMenu: CustomColumnMenuWrapper,
   //  pagination: DataGridPagination,
   ...(currentColumnsPreset?.isPro ? {} : { pagination: DataGridPagination }),
   toolbar: GridToolbar,
   ...gridSlotsIcons,
  }),
  [propSlots, currentColumnsPreset?.isPro],
 );

 return {
  groupingColDef,
  processedRows,
  allColumns,
  gridSx,
  slots,
  gridMode,
  handleRowGroupingModelChange,
  getRowClassName,
 };
};

export default useGridConfig;
